import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

function getQueryParam(search, key, fallback = "") {
    const params = new URLSearchParams(search);
    return params.get(key) || fallback;
}

function getRetryPaymentId(data) {
    return data?.payment_id || data?.payment?.id || data?.id || "";
}

function getPrimaryMessage(reason) {
    if (reason === "paym8_payment_not_completed") {
        return "PayM8 opened, but the payment was not completed.";
    }

    return "The order was created, but payment could not be started with PayM8.";
}

export default function PaymentErrorPage() {
    const location = useLocation();
    const { currentUser } = useSelector((state) => state.auth);
    const [paymentId, setPaymentId] = useState(getQueryParam(location.search, "payment_id"));
    const orderId = getQueryParam(location.search, "order_id");
    const customerCode = getQueryParam(location.search, "customer_code");
    const reason = getQueryParam(location.search, "reason");
    const [loading, setLoading] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [message, setMessage] = useState("");
    const isPaymentNotCompleted = reason === "paym8_payment_not_completed";

    async function retryPayment() {
        if (!paymentId || !currentUser?.token) {
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}payments/${paymentId}/retry-paym8`,
                headers: { Authorization: `Bearer ${currentUser.token}` },
            });

            const redirectUrl = response.data?.redirect_url;

            if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
            }

            setMessage("Payment could not be restarted. Please try again.");
        } catch (error) {
            const status = error?.response?.status;
            const data = error?.response?.data || {};
            const nextPaymentId = getRetryPaymentId(data);

            if (nextPaymentId) {
                setPaymentId(String(nextPaymentId));
            }

            if (status === 502) {
                setMessage(data.message || "PayM8 could not be reached. Please try again.");
                return;
            }

            if (status === 409) {
                setCompleted(true);
                setMessage(data.message || "Payment is already completed.");
                return;
            }

            setMessage(data.message || "Payment retry failed. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="bg-white">
            <div className="my-8 space-y-6 px-4 sm md:max-w-2xl lg:max-w-4xl xl:max-w-5xl m-auto">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <h1>Payment Error</h1>

                    <Link
                        className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_grey_one hover:bg-lk_dark_grey_button_hover uppercase text-center"
                        to="/orders"
                    >
                        Orders
                    </Link>
                </div>

                <div className={`border px-5 py-4 ${isPaymentNotCompleted ? "border-yellow-200 bg-yellow-50 text-yellow-800" : "border-red-200 bg-red-50 text-red-800"}`}>
                    <p className="font-semibold">
                        {getPrimaryMessage(reason)}
                    </p>
                    {orderId && (
                        <p className="mt-2 text-sm">
                            Order ID: <span className="font-semibold">{orderId}</span>
                        </p>
                    )}
                    {customerCode && (
                        <p className="mt-1 text-sm">
                            Customer Code: <span className="font-semibold">{customerCode}</span>
                        </p>
                    )}
                </div>

                {message && (
                    <div className={`border px-5 py-4 text-sm ${completed ? "border-green-200 bg-green-50 text-green-800" : "border-yellow-200 bg-yellow-50 text-yellow-800"}`}>
                        {message}
                    </div>
                )}

                {!completed && (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <button
                            type="button"
                            disabled={loading || !paymentId || !currentUser?.token}
                            onClick={retryPayment}
                            className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_red hover:bg-lk_red_hover uppercase disabled:opacity-50"
                        >
                            {loading ? "Retrying..." : "Retry payment"}
                        </button>

                        {!paymentId && (
                            <span className="text-sm text-gray-600">
                                Payment reference is missing.
                            </span>
                        )}

                        {!currentUser?.token && (
                            <span className="text-sm text-gray-600">
                                Sign in again to retry payment.
                            </span>
                        )}
                    </div>
                )}

                {completed && (
                    <Link className="inline-block text-sm font-medium text-lk_red hover:text-lk_red_hover" to="/orders">
                        Back to orders
                    </Link>
                )}
            </div>
        </div>
    );
}
